import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Agent } from '../../types/agent';
import { TemplateCategory, TEMPLATE_CATEGORIES } from '../../types/template';

export interface PublishTemplateFields {
  name: string;
  description: string;
  category: TemplateCategory;
  tags: string[];
}

interface PublishTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: PublishTemplateFields) => void;
  agent: Agent;
  isSubmitting?: boolean;
}

/** Pick a sensible default category from the agent's type when the names line up. */
function defaultCategory(agent: Agent): TemplateCategory {
  const match = TEMPLATE_CATEGORIES.find((c) => c.value === (agent.type as unknown as TemplateCategory));
  return match ? match.value : TEMPLATE_CATEGORIES[0].value;
}

export function PublishTemplateModal({
  isOpen,
  onClose,
  onConfirm,
  agent,
  isSubmitting = false,
}: PublishTemplateModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TemplateCategory>(TEMPLATE_CATEGORIES[0].value);
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName(agent.name);
      setDescription(agent.description ?? '');
      setCategory(defaultCategory(agent));
      setTags('');
    }
  }, [isOpen, agent]);

  if (!isOpen) return null;

  const descOk = description.trim().length >= 10;
  const canSubmit = name.trim().length > 0 && descOk && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm({
      name: name.trim(),
      description: description.trim(),
      category,
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    });
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className="relative z-50 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-modal-title"
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-1 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
              </svg>
            </div>
            <h3 id="publish-modal-title" className="text-lg font-semibold text-gray-900">
              Publish as template
            </h3>
          </div>

          <p className="mb-4 text-sm text-gray-600">
            Share <span className="font-semibold text-gray-900">"{agent.name}"</span> with the
            community. It's submitted for review and appears in the marketplace once an admin approves it.
          </p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pub-name">Template name</Label>
              <Input
                id="pub-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pub-category">Category</Label>
              <select
                id="pub-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                disabled={isSubmitting}
                className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {TEMPLATE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.icon} {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pub-desc">Description</Label>
              <textarea
                id="pub-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={2000}
                disabled={isSubmitting}
                placeholder="What does this agent do, and when should someone use it?"
                className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className={`text-xs ${description.trim().length > 0 && !descOk ? 'text-red-500' : 'text-gray-500'}`}>
                At least 10 characters — this is what people read in the marketplace.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pub-tags">Tags <span className="text-gray-400">(optional)</span></Label>
              <Input
                id="pub-tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g. support, triage, email"
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">Comma-separated, for discoverability.</p>
            </div>
          </div>

          <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Tip: don't hard-code secrets in the agent config — the template is shared with others.
          </p>

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? 'Submitting…' : 'Submit for review'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PublishTemplateModal;
